// The settings controller (bead hf-e3b-circle-people-r3z.2).
//
// One state machine over six independent saves. It knows nothing about Convex — it
// is handed the functions that perform each one, exactly as `useClaim` and
// `useCalendarFeed` are.
//
// WHY THE OUTCOME REMEMBERS WHICH SECTION IT BELONGS TO. This screen has six forms on
// it. A single "Saved." banner at the top would tell an organiser who just changed the
// timezone that *something* saved, without saying what — and on the one screen where a
// mistake is expensive, that is the wrong kind of quiet. Every outcome carries its
// `section`, so the message renders next to the form it is about.

import type { CommonRejection } from "@herefor/contracts";
import { useCallback, useRef, useState } from "react";

import type {
  SetBlockDefinitionsResult,
  SetDirectRequestExpiryPolicyResult,
  SetDisplayNameResult,
  SetPrintContactNameResult,
  SetPrintContactResult,
  SetSupportedPersonNameResult,
  SetTimeZoneResult,
} from "../../convex/circleSettings";

export type SettingsSection =
  | "planName"
  | "blocks"
  | "requestExpiry"
  | "timeZone"
  | "supportedPersonName"
  | "supportedPerson"
  | "printContactPerson";

/** Wall-clock bounds as the form holds them: plain strings, validated by the server. */
export interface BlockBoundsDraft {
  readonly startLocal: string;
  readonly endLocal: string;
}

export interface BlockDefinitionsDraft {
  readonly morning: BlockBoundsDraft;
  readonly afternoon: BlockBoundsDraft;
  readonly evening: BlockBoundsDraft;
}

export type SettingsOutcome =
  | { readonly status: "idle" }
  | { readonly status: "working"; readonly section: SettingsSection }
  | { readonly status: "saved"; readonly section: SettingsSection }
  /** Field-level problems, already turned into sentences by `settingsCopy`. */
  | {
      readonly status: "invalid";
      readonly section: SettingsSection;
      readonly issues: readonly string[];
    }
  | {
      readonly status: "refused";
      readonly section: SettingsSection;
      readonly rejection: CommonRejection;
    };

export interface UseCircleSettingsOptions {
  readonly setBlockDefinitions: (
    blockDefinitions: BlockDefinitionsDraft,
  ) => Promise<SetBlockDefinitionsResult>;
  readonly setDirectRequestExpiryPolicy: (
    unansweredTtlDays: number,
  ) => Promise<SetDirectRequestExpiryPolicyResult>;
  readonly setTimeZone: (timeZone: string) => Promise<SetTimeZoneResult>;
  readonly setDisplayName: (displayName: string) => Promise<SetDisplayNameResult>;
  readonly setSupportedPersonName: (chosenName: string) => Promise<SetSupportedPersonNameResult>;
  readonly setPrintContact: (printContactText: string) => Promise<SetPrintContactResult>;
  readonly setPrintContactName: (
    printContactPersonName: string,
  ) => Promise<SetPrintContactNameResult>;
}

export interface CircleSettingsController {
  readonly outcome: SettingsOutcome;
  readonly saveBlocks: (blockDefinitions: BlockDefinitionsDraft) => Promise<void>;
  readonly saveRequestExpiryPolicy: (unansweredTtlDays: number) => Promise<void>;
  readonly saveTimeZone: (timeZone: string) => Promise<void>;
  readonly savePlanName: (displayName: string) => Promise<void>;
  readonly saveSupportedPersonName: (chosenName: string) => Promise<void>;
  readonly savePrintContact: (printContactText: string) => Promise<void>;
  readonly savePrintContactName: (printContactPersonName: string) => Promise<void>;
  /** Clear a message. Dismisses it; undoes nothing. */
  readonly dismiss: () => void;
}

/** Stands in for "we do not know", so nothing is shown as saved that may not be. */
const TRANSPORT_FAILURE: CommonRejection = { kind: "validation_failed", issues: [] };

type AnyOutcome =
  | { status: "applied"; value: { ok: true } | { ok: false; rejection: CommonRejection } }
  | { status: "replayed" };

export function useCircleSettings({
  setBlockDefinitions,
  setDirectRequestExpiryPolicy,
  setTimeZone,
  setDisplayName,
  setSupportedPersonName,
  setPrintContact,
  setPrintContactName,
}: UseCircleSettingsOptions): CircleSettingsController {
  const [outcome, setOutcome] = useState<SettingsOutcome>({ status: "idle" });

  /**
   * One in-flight guard for the whole screen, not one per form.
   *
   * Two settings saving at once is not a race we need to support, and a shared guard
   * means the second press cannot land while the first is deciding — which on this
   * screen would mean two `settingsVersion` bumps for one intention.
   */
  const inFlight = useRef(false);

  const run = useCallback(
    async (section: SettingsSection, perform: () => Promise<AnyOutcome>): Promise<void> => {
      if (inFlight.current) return;
      inFlight.current = true;
      setOutcome({ status: "working", section });

      let next: SettingsOutcome;
      try {
        const result = await perform();
        if (result.status === "replayed") {
          next = { status: "saved", section };
        } else if (result.value.ok) {
          next = { status: "saved", section };
        } else if (
          result.value.rejection.kind === "validation_failed" &&
          result.value.rejection.issues.length > 0
        ) {
          next = { status: "invalid", section, issues: result.value.rejection.issues };
        } else {
          next = { status: "refused", section, rejection: result.value.rejection };
        }
      } catch {
        next = { status: "refused", section, rejection: TRANSPORT_FAILURE };
      }

      inFlight.current = false;
      setOutcome(next);
    },
    [],
  );

  return {
    outcome,
    saveBlocks: useCallback(
      (blockDefinitions: BlockDefinitionsDraft) =>
        run("blocks", () => setBlockDefinitions(blockDefinitions)),
      [run, setBlockDefinitions],
    ),
    saveRequestExpiryPolicy: useCallback(
      (unansweredTtlDays: number) =>
        run("requestExpiry", () => setDirectRequestExpiryPolicy(unansweredTtlDays)),
      [run, setDirectRequestExpiryPolicy],
    ),
    saveTimeZone: useCallback(
      (timeZone: string) => run("timeZone", () => setTimeZone(timeZone)),
      [run, setTimeZone],
    ),
    savePlanName: useCallback(
      (displayName: string) => run("planName", () => setDisplayName(displayName)),
      [run, setDisplayName],
    ),
    saveSupportedPersonName: useCallback(
      (chosenName: string) => run("supportedPersonName", () => setSupportedPersonName(chosenName)),
      [run, setSupportedPersonName],
    ),
    savePrintContact: useCallback(
      (printContactText: string) => run("supportedPerson", () => setPrintContact(printContactText)),
      [run, setPrintContact],
    ),
    savePrintContactName: useCallback(
      (printContactPersonName: string) =>
        run("printContactPerson", () => setPrintContactName(printContactPersonName)),
      [run, setPrintContactName],
    ),
    dismiss: useCallback(() => {
      inFlight.current = false;
      setOutcome({ status: "idle" });
    }, []),
  };
}
